"use client"

/**
 * Six mistakes that sink a seeding job. Sits below the prep checklist on
 * /tools/seeding-calculator. Copy from the Claude Design polish.
 */

interface Mistake {
  title: string
  body: string
}

const MISTAKES: Mistake[] = [
  {
    title: "Seeding too early in fall",
    body:
      "Summer heat fries seedlings. Wait for daytime highs in the 70s and consistent overnight cooling. Go by that, not a calendar date.",
  },
  {
    title: "Seeding too late",
    body:
      "New grass needs about six weeks of mild weather to root before frost. Past the cutoff you’re feeding birds. The window above bakes in your zone’s frost dates.",
  },
  {
    title: "Skipping the rake",
    body:
      "Seed on top of thatch or compacted soil is seed in the wrong place. Scratch the surface first so it can reach bare dirt, no matter how the lawn looks.",
  },
  {
    title: "Letting it dry out",
    body:
      "A single hot afternoon can kill a seeding once the seed has cracked. Water lightly twice a day for the first two weeks, then taper as it establishes.",
  },
  {
    title: "Mowing too soon",
    body:
      "New grass needs to hit 3–4 inches before its first cut, and even then only the top third. Cutting earlier rips young roots out of the soil.",
  },
  {
    title: "Pre-emergent + seed at the same time",
    body:
      "Crabgrass preventer prevents your seed too. Skip the spring pre-emergent in any area you’re overseeding, or use one of the few seed-safe formulas (mesotrione).",
  },
]

export function SeedingMistakes() {
  return (
    <section aria-labelledby="seeding-mistakes-heading" className="text-gray-900">
      <h2 id="seeding-mistakes-heading" className="text-fluid-h2 font-bold tracking-tight text-gray-900">
        Six mistakes that kill a seeding
      </h2>
      <ul className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
        {MISTAKES.map((m) => (
          <li key={m.title} className="rounded-2xl border border-gray-200 bg-white px-5 py-4.5 shadow-elevation-2">
            <div className="mb-2 flex items-start gap-2.5">
              <span
                className="mt-px inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600"
                aria-hidden="true"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </span>
              <h3 className="text-[15px] font-semibold leading-snug text-gray-900">{m.title}</h3>
            </div>
            <p className="text-[13.5px] leading-relaxed text-gray-700">{m.body}</p>
          </li>
        ))}
      </ul>
    </section>
  )
}
